"use client"

import { useState, useEffect } from "react"
import { Users, Clock, ArrowRight, UserCheck, Database } from "lucide-react"
import { cn } from "@/lib/utils"
import { Link, useSearchParams } from "react-router-dom"
import { collection, query, orderBy, getDocs } from "firebase/firestore"
import { db } from "@/lib/firebase"
import { motion } from "framer-motion"
import { useInView } from "@/hooks/use-in-view"
import { usePortal } from "@/hooks/use-portal"

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
}
const cardAnim = {
  hidden: { opacity: 0, y: 36 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
}

export default function FeaturedCourses() {
  const { portal, setPortal } = usePortal()
  const [filter, setFilter] = useState("all")
  const [courses, setCourses] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [searchParams, setSearchParams] = useSearchParams()
  const urlPortal = searchParams.get("portal")

  // Sync internal filter with global portal and URL
  useEffect(() => {
    if (urlPortal === "tspsc" || urlPortal === "appsc") {
      setPortal(urlPortal as any)
      setFilter(urlPortal)
      
      // Force scroll on portal change from URL if it's the only change
      const element = document.getElementById('courses')
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    } else {
      setFilter(portal)
    }
  }, [portal, urlPortal, setPortal])

  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter)
    if (newFilter === 'tspsc' || newFilter === 'appsc') {
      setPortal(newFilter as any)
      setSearchParams({ portal: newFilter }, { replace: true })
    } else {
      const newParams = new URLSearchParams(searchParams)
      newParams.delete("portal")
      setSearchParams(newParams, { replace: true })
    }
  }

  // Hydrate from Firestore
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const q = query(collection(db, "courses"), orderBy("createdAt", "desc"))
        const querySnapshot = await getDocs(q)
        
        if (!querySnapshot.empty) {
          const fetchedData = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          setCourses(fetchedData)
        } else {
          setCourses([])
        }
      } catch (error) {
         console.error("Database fetch failed.", error)
         setCourses([])
      } finally {
        setLoading(false)
      }
    }
    fetchCourses()
  }, [])

  const filteredCourses = courses.filter(
    course => {
      // 1. FILTER HIDDEN
      if (course.isVisible === false) return false;

      // 2. PORTAL FILTER (Supports legacy tspc/appc and new tspsc/appsc)
      const isTspsc = course.portal === "tspsc" || course.portal === "tspc";
      const isAppsc = course.portal === "appsc" || course.portal === "appc";
      const isAll = !course.portal || course.portal === "all";

      if (filter === "tspsc") return isTspsc || isAll;
      if (filter === "appsc") return isAppsc || isAll;
      return true; // "all" filter
    }
  )

  const { ref: headRef, inView: headIn } = useInView()

  return (
    <section id="courses" className="pt-8 pb-4 sm:pt-20 sm:pb-10 bg-slate-50 border-y border-slate-200">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
        
          <motion.div 
            ref={headRef}
            className="flex flex-col items-center text-center space-y-3 mb-10 sm:mb-12"
            initial={{ opacity: 0, y: 24 }}
            animate={headIn ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
             <div className="flex items-center justify-center gap-4 mb-1">
                  <div className="w-10 h-px bg-[#b48c36]/30" />
                  <p className="text-[#b48c36] font-bold text-[10px] sm:text-[11px] uppercase tracking-[0.4em]">Specialized Modules</p>
                  <div className="w-10 h-px bg-[#b48c36]/30" />
               </div>
            <h2 className="text-4xl md:text-5xl lg:text-[48px] font-serif font-medium tracking-tight text-slate-950 leading-tight">
              Targeted <span className=" font-light text-[#b48c36]">Masterclasses.</span>
            </h2>
          </motion.div>

          {/* Academic Ledger Filters - Luxury Minimalist Box with Ambient Tint */}
          <div className="flex justify-center mb-12 relative z-10 px-4">
            <div className={cn(
              "inline-flex items-center p-1 rounded-sm gap-1 transition-all duration-700 border shadow-2xl shadow-slate-200/20",
              filter === "all" ? "bg-[#b48c36]/5 border-[#b48c36]/20" : 
              filter === "tspsc" ? "bg-blue-50/80 border-blue-200/50" : 
              "bg-emerald-50/80 border-emerald-200/50"
            )}>
              {["all", "tspsc", "appsc"].map((cat) => {
                const isActive = filter === cat;
                const activeColors = {
                  all:   "text-[#b48c36]",
                  tspsc: "text-blue-800",
                  appsc: "text-emerald-800"
                };
                
                return (
                  <button
                    key={cat}
                    onClick={() => handleFilterChange(cat)}
                    className={cn(
                      "group px-6 sm:px-10 py-2.5 transition-all duration-500 relative rounded-sm",
                      isActive
                        ? "bg-white shadow-md"
                        : "text-slate-400 hover:text-slate-900 hover:bg-white/40"
                    )}
                  >
                    <span className={cn(
                      "text-xs sm:text-sm font-serif font-medium uppercase tracking-[0.15em]",
                      isActive ? activeColors[cat as keyof typeof activeColors] : ""
                    )}>
                      {cat === "all" ? "All Divisions" : cat}
                    </span>
                    
                    {isActive && (
                      <motion.div 
                        layoutId="activeFilterIndicator"
                        className={cn(
                          "absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-[2px]",
                          cat === "all" ? "bg-[#b48c36]" : cat === "tspsc" ? "bg-blue-800" : "bg-emerald-800"
                        )} 
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

        {loading ? (
          <div className="w-full flex flex-col items-center justify-center py-32 space-y-4 opacity-50">
             <Database className="w-8 h-8 text-[#b48c36] animate-pulse" />
             <p className="text-[#b48c36] font-bold text-[10px] uppercase tracking-[0.4em]">Querying Academy Database...</p>
          </div>
        ) : (
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filteredCourses.map((course) => (
              <motion.div
                key={course.id}
                variants={cardAnim}
                className="group flex flex-col bg-white border border-slate-100 overflow-hidden hover:shadow-[0_24px_48px_-12px_rgba(0,0,0,0.1)] transition-all duration-500 relative"
              >
                {/* ── Image ── */}
                {(() => {
                  const img = course.image || course.imageUrl;
                  const isDummy = !img || img.includes("unsplash.com") || img.includes("placeholder.svg");
                  
                  if (!isDummy) {
                    return (
                      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
                        <img
                          src={img}
                          alt={course.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                        {/* Gradient overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent" />
                        {/* Price pill */}
                        <div className="absolute bottom-3 right-3">
                          {course.type === "free" ? (
                            <span className="bg-emerald-600 text-white text-[9px] font-black uppercase tracking-widest px-3 py-1">Free</span>
                          ) : (
                            <span className="bg-[#b48c36] text-white text-[9px] font-black uppercase tracking-widest px-3 py-1">
                              ₹{Number(course.fees || course.discountedPrice || 0).toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  }

                  // Professional Wireframe Placeholder
                  return (
                    <div className="relative aspect-[4/3] overflow-hidden bg-slate-950 flex flex-col items-center justify-center border-b border-white/5">
                       {/* Abstract Background Patterns */}
                       <div className="absolute inset-0 opacity-20">
                          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(#ffffff10_1px,transparent_1px)] [background-size:20px_20px]" />
                          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-transparent to-emerald-900/20" />
                       </div>
                       
                       <div className="relative z-10 flex flex-col items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-[#b48c36]">
                             <Database size={24} strokeWidth={1.5} />
                          </div>
                       </div>

                       {/* Price pill for placeholder */}
                       <div className="absolute bottom-3 right-3">
                          {course.type === "free" ? (
                            <span className="bg-emerald-600 text-white text-[9px] font-black uppercase tracking-widest px-3 py-1">Free</span>
                          ) : (
                            <span className="bg-[#b48c36] text-white text-[9px] font-black uppercase tracking-widest px-3 py-1">
                              ₹{Number(course.fees || course.discountedPrice || 0).toLocaleString()}
                            </span>
                          )}
                        </div>
                    </div>
                  );
                })()}

                {/* Badges (Floating outside the image container logic to stay consistent) */}
                <div className="absolute top-3 left-3 flex gap-1.5 z-20">
                  {course.tag && (
                    <span className="bg-white/15 backdrop-blur-md border border-white/20 text-white text-[8px] font-bold uppercase tracking-widest px-2.5 py-1">
                      {course.tag}
                    </span>
                  )}
                  {course.portal && course.portal !== "all" && (
                    <span className={cn(
                      "text-[8px] font-black uppercase tracking-widest px-2.5 py-1 text-white",
                      course.portal === "tspsc" ? "bg-blue-600" : "bg-emerald-700"
                    )}>
                      {course.portal.toUpperCase()}
                    </span>
                  )}
                </div>

                {/* ── Content ── */}
                <div className="p-5 flex flex-col flex-grow">
                  {/* Meta row */}
                  <div className="flex items-center justify-between mb-3">
                    <span className="flex items-center gap-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                      <UserCheck size={11} className="text-[#b48c36]" />
                      {course.instructor}
                    </span>
                    <span className="flex items-center gap-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                      <Users size={10} />
                      {course.scholars || "0"}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="font-serif text-lg font-semibold text-slate-900 leading-snug group-hover:text-[#b48c36] transition-colors duration-300 mb-4 line-clamp-2 flex-grow">
                    {course.title}
                  </h3>

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-4 border-t border-slate-50">
                    <div className="flex items-center gap-1.5 text-[10px] text-slate-400 font-medium uppercase tracking-widest">
                      <Clock size={11} />
                      {course.duration}
                    </div>
                    <Link
                      to={`/courses/${course.slug || course.id}`}
                      className={cn(
                        "text-white text-[9px] font-black uppercase tracking-[0.2em] px-4 py-2 flex items-center gap-1.5 transition-all",
                        course.portal === "tspsc" ? "bg-blue-900 hover:bg-blue-700" :
                        course.portal === "appsc" ? "bg-emerald-900 hover:bg-emerald-700" :
                        "bg-slate-950 hover:bg-[#b48c36]"
                      )}
                    >
                      Buy Now <ArrowRight size={11} />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Missing state or Link */}
        {!loading && filteredCourses.length === 0 && (
           <div className="w-full py-12 text-center">
              <p className="text-sm font-medium text-slate-500 ">No modules identified for this exam specification in our database.</p>
           </div>
        )}



      </div>
    </section>
  )
}
